const _ = require("lodash");

const {FLAGS} = require("../util/GlobalFlags");
const Font = require("../util/Font");
const Math2D = require("../util/Math2D");
const CanvasUtil = require("../util/CanvasUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");
const ColorUtil = require("../util/ColorUtil");

const FastCanvasMath = require("./FastCanvasMath");

const Parser = require("./Parser");
const RectChecks = require("./RectChecks");
const RC = require("./util/RenderController");
const Nodes = require("./Nodes");
const Helpers = require("./util/RenderHelpers");
const ParserHelpers = require("./util/ParserHelpers");
const Diagram = require("./Diagram");
const BatexAloud = require("./BatexAloud");

const injectDependencies = function(DiagramHelpers) {
	Diagram.injectDependencies(DiagramHelpers);
};

// The entry points.

// Pass in a canvas context and an object with possible properties:
// - batex: Required. String of batex to render.
// - rect: Required. Rectangle to draw inside of in [x, y, w, h] form. The
//   math will be centered inside this rectangle and shrunk to fit if needed.
// - fontInfo: Optional. Object with size and family keys. If not provided
//   it is computed from the current context.font value.
// - wrapWidth: Optional. Wordwrapping will be done when lines exceed this
//   with. Defaults to the width of the rect.
// - scaleOff: Optional. Set to true to prevent scaling in the event the batex
//   does not fit inside the rect.
// - scaleDownOnly: Optional. Set to true to prevent scaling by a factor greater
//   than 1. No effect if scaleOff is true.
// - distort: Optional. Set to true to allow scaling to distort the font.
// - centered: Optional. Set to true to center the drawn content.
// - forceFullWidth: Optional. Set to true to force lines and paragraphs to
//   be as wide as the wrapWidth.
// - skipLinePadding: Optional. Set to true to make line heights exactly
//   equal to the font size.
// - skipBlockPadding: Optional. Set to true to not draw top and bottom padding
//   around block nodes.
// - overrideBlankSize: Optional. An object with w and h that will be used for
//   the sizing of all blanks
// - largestBlankCharacter: Optional. The largest width character that needs to
//   appear in any blanks in the Batex (defaults to "0")
// - blanksContainOperators: Optional. True if blanks may to contain operators
// - insetBlurSize: Optional. Pass blur size for outset shadows drawn by the
//   fakeblank command. Defaults to 0.
// - rulerFontSize: Optional. Pass the font size used for diagrams that use
//   special ruler types.
// - customCommands: Optional. Object mapping custom command names to objects
//   with measure, draw methods.
// - customEnvironments: Optional. Object mapping custom environment names to
//   objects with measure, draw methods.
// - debugBoxes: Optional. Set to true to draw blue boxes around every single
//   node. Also disables fast rendering if set to true.
// - noFast: Optional. Set to true to disable fast rendering.
// - computedFullMeasure: Optional. Pass a return of fullMeasureBatex as an
//   optimization to have this method skip the measuring and use this instead.
// - computedFastMeasure: Optional. Pass the fastMeasure value of a return of
//   a FastCanvasMath method to signal to use FastCanvasMath and to use this
//   value to skip some computations.
// - computedParseResult: Optional. If included, skips the parsing step.
// Returns an object with blanks and errors arrays.
const drawInRect = function(context, props) {
	const fast =
		!props.debugBoxes &&
		!props.noFast &&
		FastCanvasMath.drawInRect(context, props);
	if (fast) {
		return fast;
	}

	props = _.extend({}, props, {
		wrapWidth: props.wrapWidth === undefined ? props.rect[2] : props.wrapWidth,
		willBeFit: true,
	});

	const fullMeasure = fullMeasureBatex(context, props);
	const baseNode = fullMeasure.node;
	const rc = fullMeasure.rc.clone();
	const baseMeasure = rc.getMeasure(baseNode);
	rc.debugBoxes = !!props.debugBoxes;
	rc.topNodeWidth = baseMeasure.width;

	context.save();
	context.textBaseline = "alphabetic";

	const rectContent = [0, 0, baseMeasure.width, baseMeasure.height];
	const fit = Math2D.fitInRect(rectContent, props.rect, {
		scaleOff: props.scaleOff,
		scaleDownOnly: props.scaleDownOnly,
		distort: props.distort,
	});
	context.translate(fit.translate[0], fit.translate[1]);
	context.scale(fit.scale[0], fit.scale[1]);

	if (FLAGS.enableCanvasMathRectChecks) {
		RectChecks.begin(rc);
	}
	drawNode(rc, 0, 0, baseNode, baseMeasure);
	if (FLAGS.enableCanvasMathRectChecks) {
		RectChecks.end(rc, 0, 0, baseNode, baseMeasure);
	}
	context.restore();

	const measure = _.extend({}, baseMeasure, {
		width: fit.fitRect[2],
		height: fit.fitRect[3],
	});

	if (rc.errors.length) {
		ErrorUtil.log("E_CANVASMATH", "CanvasMath drawInRect errors", rc.errors);
	}
	return {
		measure: measure,
		scale: fit.scale,
		rect: fit.fitRect,
		blanks: rc.blanks,
		errors: rc.errors,
		unused: fit.unused,
	};
};

// Pass in a canvas context and an object with possible properties:
// - batex: Required. String of batex to render.
// - x: Required. x position of top left corner.
// - y: Required. y position of top left corner.
// - fontInfo: Optional. Object with size and family keys. If not provided
//   it is computed from the current context.font value.
// - wrapWidth: Optional. Wordwrapping will be done when lines exceed this
//   with. Defaults to no wrapping.
// - minScale: Optional. Scaling overly long lines to fit will throw errors
//   if it goes below this threshold. Default 0.75.
// - centered: Optional. Set to true to center the drawn content.
// - forceFullWidth: Optional. Set to true to force lines and paragraphs to
//   be as wide as the wrapWidth, if provided.
// - skipLinePadding: Optional. Set to true to make line heights exactly
//   equal to the font size.
// - skipBlockPadding: Optional. Set to true to not draw top and bottom padding
//   around block nodes.
// - overrideBlankSize: Optional. An object with w and h that will be used for
//   the sizing of all blanks
// - largestBlankCharacter: Optional. The largest width character that needs to
//   appear in any blanks in the Batex (defaults to "0")
// - blanksContainOperators: Optional. True if blanks may to contain operators
// - insetBlurSize: Optional. Pass blur size for outset shadows drawn by the
//   fakeblank command. Defaults to 0.
// - rulerFontSize: Optional. Pass the font size used for diagrams that use
//   special ruler types.
// - customCommands: Optional. Object mapping custom command names to objects
//   with measure, draw methods.
// - customEnvironments: Optional. Object mapping custom environment names to
//   objects with measure, draw methods.
// - debugBoxes: Optional. Set to true to draw blue boxes around every single
//   node. Also disables fast rendering if set to true.
// - noFast: Optional. Set to true to disable fast rendering.
// - computedFullMeasure: Optional. Pass a return of fullMeasureBatex as an
//   optimization to have this method skip the measuring and use this instead.
// - computedFastMeasure: Optional. Pass the fastMeasure value of a return of
//   a FastCanvasMath method to signal to use FastCanvasMath and to use this
//   value to skip some computations.
// - computedParseResult: Optional. If included, skips the parsing step.
// Returns an object with blanks and errors arrays.
const render = function(context, props) {
	const fast =
		!props.debugBoxes && !props.noFast && FastCanvasMath.render(context, props);
	if (fast) {
		return fast;
	}

	const fullMeasure = fullMeasureBatex(context, props);
	const baseNode = fullMeasure.node;
	const rc = fullMeasure.rc.clone();
	const baseMeasure = rc.getMeasure(baseNode);
	rc.debugBoxes = !!props.debugBoxes;
	rc.topNodeWidth = baseMeasure.width;

	context.save();
	context.textBaseline = "alphabetic";
	if (FLAGS.enableCanvasMathRectChecks) {
		RectChecks.begin(rc);
	}
	drawNode(rc, props.x, props.y, baseNode, baseMeasure);
	if (FLAGS.enableCanvasMathRectChecks) {
		RectChecks.end(rc, props.x, props.y, baseNode, baseMeasure);
	}
	context.restore();

	if (rc.errors.length) {
		ErrorUtil.log("E_CANVASMATH", "CanvasMath render errors", rc.errors);
	}
	return {
		measure: baseMeasure,
		blanks: rc.blanks,
		errors: rc.errors,
	};
};

// The public-facing version of fullMeasureBatex, doing the exact same thing
// except returning an object with measure and errors. The only difference is
// that this might go through FastCanvasMath instead, so the only extra props
// it offers are debugBoxes, noFast, and computedFastMeasure. See render or
// drawInRect for what these do.
const measureBatex = function(context, props) {
	context = context || CanvasUtil.getTransientContext();
	const fast =
		!props.debugBoxes &&
		!props.noFast &&
		FastCanvasMath.measureBatex(context, props);
	if (fast) {
		return fast;
	}

	const result = fullMeasureBatex(context, props);
	return {
		measure: result.rc.getMeasure(result.node),
		errors: result.rc.errors,
	};
};

// Only parses the batex. Pass in an object with possible properties:
// - batex: Required. String of batex to parse.
// - customCommands: Optional. Object mapping custom command names to objects
//   with measure, draw methods.
// - customEnvironments: Optional. Object mapping custom environment names to
//   objects with measure, draw methods.
// Returns an object with a tree key giving the root node and an errors array.
const getParseTree = function(props) {
	return parseBatex(props.batex, props);
};

// Parses the batex and returns a list of parsed blanks
// - batex: Required. String of batex to parse blanks from.
// - rulerFontSize: Optional. Pass the font size used for diagrams that use
//   special ruler types.
// - customCommands: Optional. Object mapping custom command names to objects
//   with measure, draw methods.
// - customEnvironments: Optional. Object mapping custom environment names to
//   objects with measure, draw methods.
// - computedFullMeasure: Optional. Pass a return of fullMeasureBatex as an
//   optimization to have this method skip the measuring and use this instead.
// - computedFastMeasure: Optional. Signals that this batex can be rendered
//   fast, which means it has no blanks.
// - computedParseResult: Optional. If included, skips the parsing step.
// Returns an array of blank nodes
const getBlankList = function(props) {
	const context = CanvasUtil.getTransientContext();
	if (props.computedFastMeasure || FastCanvasMath.isFast(context, props)) {
		return [];
	}
	const fullMeasure = fullMeasureBatex(context, props);
	return getBlanksInNode(fullMeasure.rc, fullMeasure.node);
};

// Used by automated tests only.
const resetInternalCaches = function() {
	Helpers.resetTextWidthCache();
};

const createRC = function(context) {
	return new RC(context, {
		measureNode,
		drawNode,
		isSpaceNode: ParserHelpers.isSpaceNode,
		isBlockNode,
	});
};

const isBlockNode = function(node) {
	return !!(
		node &&
		Nodes.nodeData[node.type] &&
		Nodes.nodeData[node.type].isBlock
	);
};

// Top-level helper functions

// Helper for all of the public facing methods. This only returns the
// measurement of the top level paragraph node without drawing anything. The
// context is optional. Pass in a canvas context and an object with possible
// properties:
// - batex: Required. String of batex to measure.
// - fontInfo: Optional. Object with size and family keys. If not provided
//   it is computed from the current context.font value.
// - wrapWidth: Optional. Wordwrapping will be done when lines exceed this
//   with. Defaults to no wrapping.
// - willBeFit: Optional. Set to true if long lines will be scaled down after
//   the measure call.
// - minScale: Optional. Scaling overly long lines to fit will throw errors if
//   it goes below this threshold. Default 0.75. Ignored if willBeFit is true.
// - centered: Optional. Set to true to center the drawn content.
// - forceFullWidth: Optional. Set to true to force lines and paragraphs to
//   be as wide as the wrapWidth.
// - skipLinePadding: Optional. Set to true to make line heights exactly
//   equal to the font size.
// - skipBlockPadding: Optional. Set to true to not draw top and bottom padding
//   around block nodes.
// - overrideBlankSize: Optional. An object with w and h that will be used for
//   the sizing of all blanks
// - largestBlankCharacter: Optional. The largest width character that needs to
//   appear in any blanks in the Batex (defaults to "0")
// - blanksContainOperators: Optional. True if blanks may to contain operators
// - insetBlurSize: Optional. Pass blur size for outset shadows drawn by the
//   fakeblank command. Defaults to 0.
// - rulerFontSize: Optional. Pass the font size used for diagrams that use
//   special ruler types.
// - customCommands: Optional. Object mapping custom command names to objects
//   with measure, draw methods.
// - customEnvironments: Optional. Object mapping custom environment names to
//   objects with measure, draw methods.
// - computedFullMeasure: Optional. If included, it will be immediately returned
//   without this function doing any computation. For composability purposes.
// - computedParseResult: Optional. If included, skips the parsing step.
// Returns an object with a node containing all measures, the props sent to the
// measure call, and the render controller which has errors inside of it.
const fullMeasureBatex = function(context, props) {
	if (props.computedFullMeasure) {
		return props.computedFullMeasure;
	}

	context = context || CanvasUtil.getTransientContext();
	const rc = createRC(context);

	context.save();
	context.textBaseline = "alphabetic";

	// Parse the Batex and save the errors.
	const parseResult =
		props.computedParseResult || parseBatex(props.batex, props);
	rc.mergeErrors(parseResult.errors || []);
	const baseNode = parseResult.tree;

	const fontInfo = _.extend({}, Font.toInfo(context), props.fontInfo || {});
	const propsForMeasure = _.pick(props, [
		"customCommands",
		"customEnvironments",
		"overrideBlankSize",
		"largestBlankCharacter",
		"forceFullWidth",
		"skipLinePadding",
		"skipBlockPadding",
		"centered",
		"willBeFit",
		"blanksContainOperators",
		"measureWithoutOperators",
		"keyboardContainsOnlyLetters",
		"insetBlurSize",
		"rulerFontSize",
	]);
	if (!props.willBeFit) {
		propsForMeasure.minScale = props.minScale || 0.75;
	}
	propsForMeasure.largestBlankCharacter =
		propsForMeasure.largestBlankCharacter || "0";
	propsForMeasure.maxLineWidth = props.wrapWidth || 0;
	propsForMeasure.baseFontSize = fontInfo.size;
	// For use in picture commands (clock, balance scales, etc.). It is equal to the scale factor in diagrams.
	propsForMeasure.sizeScaleFactor = fontInfo.size / 20;
	measureNode(rc, fontInfo, propsForMeasure, baseNode);
	context.restore();

	if (rc.errors.length) {
		ErrorUtil.log(
			"E_CANVASMATH",
			"CanvasMath fullMeasureBatex errors",
			rc.errors
		);
	}

	return {
		node: baseNode,
		parseResult,
		props: propsForMeasure,
		rc,
	};
};

// Wrapper around Parser.parseBatex which used to incorporate caching and fast-
// track parsing. Now a simple function since those were replaced.
const parseBatex = function(batex, props) {
	return Parser.parseBatex(batex, {
		customCommands: props.customCommands,
		customEnvironments: props.customEnvironments,
	});
};

// Recursively searches through a measured node and its children to return a
// list of blanks in order, like what the main drawing functions would return.
const getBlanksInNode = function(rc, node) {
	let blankList = [];
	if (node.type === "blank") {
		blankList.push(convertBlankNode(rc, node));
	} else if (node.type === "blockDiagram") {
		blankList = blankList.concat(rc.getMeasure(node).diagramBlanks);
	} else if (node.type === "numberline" && node.numberlineInfo) {
		const info = node.numberlineInfo;
		if (info.arrows && info.arrows.length > 0) {
			info.arrows.forEach(a => {
				if (a.label) {
					blankList = blankList.concat(getBlanksInNode(rc, a.label));
				}
			});
		}
		if (info.labels && info.labels.length > 0) {
			info.labels.forEach(l => {
				blankList = blankList.concat(getBlanksInNode(rc, l.labelNode));
			});
		}
	} else if (node.type === "list" && node.listData) {
		const listData = node.listData;
		if (listData.length > 0) {
			listData.forEach(d => {
				blankList = blankList.concat(getBlanksInNode(rc, d));
			});
		}
	}
	if (node.children && node.children.length) {
		for (let i = 0; i < node.children.length; i += 1) {
			blankList = blankList.concat(getBlanksInNode(rc, node.children[i]));
		}
	}
	return blankList;
};

// Given a measured node of type "blank", convert it into the
// object returned in the blanks array of render and drawInRect, minus any
// positioning info. Must be kept in some sync with the Nodes methods for blank.
const convertBlankNode = function(rc, node) {
	const defaultRect = {x: 0, y: 0, width: 0, height: 0};
	const measure = rc.getMeasure(node);
	if (node.blankType === "fraction") {
		return _.extend(
			{
				type: "fraction",
				typeInfo: {
					numerator: _.clone(defaultRect),
					denominator: _.clone(defaultRect),
				},
				answerSize: node.answerSize,
				initialValue: node.initialValue,
				fontSizeMultiplier: measure.fontSizeMultiplier,
				fracSizeMultiplier: measure.fractionSizingRatio,
			},
			defaultRect
		);
	} else if (node.blankType === "mixed") {
		return _.extend(
			{
				type: "mixed",
				typeInfo: {
					whole: _.clone(defaultRect),
					numerator: _.clone(defaultRect),
					denominator: _.clone(defaultRect),
				},
				wholeSize: node.wholeSize,
				fracSize: node.fracSize,
				initialValue: node.initialValue,
				fontSizeMultiplier: measure.fontSizeMultiplier,
				fracSizeMultiplier: measure.fractionSizingRatio,
			},
			defaultRect
		);
	} else {
		return _.extend(
			{
				type: "simple",
				typeInfo: {},
				answerSize: node.answerSize,
				initialValue: node.initialValue,
				isExponent: !!node.isExponent,
				fontSizeMultiplier: measure.fontSizeMultiplier,
				backgroundColor: node.shaded
					? ColorUtil.getCurrentScheme().lighterGrayFill
					: null,
				outlineColor: ColorUtil.translateToDiagramColor(node.outlineColor),
			},
			defaultRect
		);
	}
};

// Drawing/measuring helper functions.

// For debugging purposes, enabled by debugBoxes in top-level props.
const drawDebugBox = function(rc, x, y, node, measure) {
	const context = rc.context;
	context.save();
	context.beginPath();
	context.strokeStyle = "#0000FF";
	if (measure.hasOwnProperty("height")) {
		context.strokeRect(x, y, measure.width, measure.height);
	} else if (measure.hasOwnProperty("top")) {
		context.strokeRect(
			x,
			y - measure.top,
			measure.width,
			measure.top + measure.bottom
		);
	}
	context.beginPath();
	context.fillStyle = "#FF0000";
	context.arc(x, y, 3, 0, 2 * Math.PI);
	context.fill();
	context.restore();
};

// Mark a node as having an error.
const drawErrorBox = function(rc, x, y, node, measure) {
	const context = rc.context;
	context.save();
	context.strokeStyle = "#FF0000";
	context.lineWidth = 2;
	if (measure.hasOwnProperty("height")) {
		context.strokeRect(x, y, measure.width, measure.height);
	} else if (measure.hasOwnProperty("top")) {
		context.strokeRect(
			x,
			y - measure.top,
			measure.width,
			measure.top + measure.bottom
		);
	}
	context.beginPath();
	context.restore();
};

// Main drawing function; passes off to appropriate node draw method.
const drawNode = function(rc, x, y, node, measure) {
	const data = Nodes.nodeData[node.type];
	if (!data) {
		throw new Error("Unrecognized node type " + node.type);
	}

	// Measure is optional to pass
	measure = measure || rc.getMeasure(node);
	if (rc.isErrored(node) && global.SHOW_ERROR_BOX) {
		drawErrorBox(rc, x, y, node, measure);
	} else if (rc.debugBoxes) {
		drawDebugBox(rc, x, y, node, measure);
	}
	data.draw(rc, x, y, node, measure);
	if (FLAGS.enableCanvasMathRectChecks) {
		RectChecks.addNode(rc, x, y, node, measure);
	}
};

// Main measure function; passes off to appropriate node measure method.
// For text-level nodes, must return width, top, bottom keys.
// For block-level nodes, must return width, height keys.
// They can also add whatever information they want so that the drawing
// function can use it.
const measureNode = function(rc, fontInfo, props, node) {
	const existingMeasure = rc.getMeasure(node);
	if (existingMeasure) {
		return existingMeasure;
	}

	const data = Nodes.nodeData[node.type];
	if (!data) {
		throw new Error("Unrecognized node type " + node.type);
	}

	const measure = data.measure(rc, fontInfo, props, node);
	rc.setMeasure(node, measure);
	return measure;
};

module.exports = {
	drawInRect,
	render,
	measureBatex,
	getParseTree,
	getBlankList,
	isFast: FastCanvasMath.isFast,
	// Don't use this unless you really need it.
	fullMeasureBatex,
	resetInternalCaches,
	injectDependencies,
	readAloudBatexText: BatexAloud.readAloudBatexText,
	readAloudBatexTree: BatexAloud.readAloudBatexTree,
};
